/**
 * Platform-aware rendering of the command palette keyboard shortcut.
 *
 * The palette opens with ⌘K on macOS and Ctrl+K on Windows/Linux (ninja-keys'
 * default `openHotkey`). These pure helpers only decide *which* modifier to
 * show — the caller owns *whether* to show a shortcut at all (device gating).
 * By the time a platform string reaches here it has already passed that gate,
 * so in practice only desktop-like platforms arrive (including iPadOS, which
 * reports `MacIntel`); `Mac` is the only branch that matters.
 *
 * `platform` is `navigator.platform` — deprecated, but its replacement
 * `navigator.userAgentData.platform` isn't available in Safari, so it stays the
 * pragmatic choice. Passing it in keeps these functions free of globals and
 * unit-testable.
 */

const APPLE_PLATFORM_PATTERN = /^Mac/i;

/** True for Apple platforms (macOS, and iPadOS which reports `MacIntel`), which use ⌘ rather than Ctrl. */
export const isApplePlatform = (platform: string): boolean => APPLE_PLATFORM_PATTERN.test(platform);

/** The ordered shortcut keys as display glyphs, for the badges and tooltip. */
export const getCommandPaletteShortcutKeys = (platform: string): string[] =>
  isApplePlatform(platform) ? ['⌘', 'K'] : ['Ctrl', 'K'];

/** The shortcut as canonical key names for `aria-keyshortcuts` (e.g. `Meta+K`, not the ⌘ glyph). */
export const getCommandPaletteAriaShortcut = (platform: string): string =>
  isApplePlatform(platform) ? 'Meta+K' : 'Control+K';
